import { useId } from 'react'

import { formatValue, type FormatOptions, type ValueFormat } from './format'

export interface HorizontalBarRow {
  /** Unique within the chart. */
  key: string
  label: string
  /** The bar's value; null draws no bar and shows `nullLabel`, never a 0. */
  value: number | null
  /** What a null value reads as, e.g. "No spend". Defaults to "—". */
  nullLabel?: string
  /** A secondary figure after the value, already formatted: "£126 spend". */
  detail?: string
}

export interface HorizontalBarChartProps {
  /** The chart's caption, which names the figure (and so its list): say what is measured and whose figure it is. */
  title: string
  /** Rendered in the order given: sort them first (the caller knows the ranking's rule, ties included). */
  rows: readonly HorizontalBarRow[]
  format: ValueFormat
  formatOptions?: FormatOptions
  /** Shown instead of the list when there are no rows. */
  emptyMessage?: string
}

/** A bar's share of the longest, in percent: a positive value is never thinner than 1%; zero, negative or none is no bar. */
export function barShare(value: number | null, top: number): number {
  if (value === null || value <= 0 || top <= 0) return 0
  return Math.max(1, (value / top) * 100)
}

/**
 * One horizontal bar per row, a single series against a zero baseline — a ranking
 * (top campaigns by ROAS, creatives by spend). Each row's label and value are text
 * beside the bar, in text ink, so the list itself is the readout: a screen reader
 * hears "Dream Car Draw: 3.10×", and the bars are decoration (`aria-hidden`). Bars
 * are SVG scaled to their track, so the chart narrows to a phone without a scroll.
 */
export function HorizontalBarChart({ title, rows, format, formatOptions, emptyMessage = 'Nothing to chart in this period.' }: HorizontalBarChartProps) {
  const captionId = useId()
  const top = Math.max(0, ...rows.map((row) => row.value ?? 0))
  const show = (row: HorizontalBarRow) => (row.value === null ? (row.nullLabel ?? '—') : formatValue(row.value, format, formatOptions))

  return (
    <figure className="ci-hbar" aria-labelledby={captionId}>
      <figcaption id={captionId} className="ci-hbar__caption">
        {title}
      </figcaption>
      {rows.length === 0 ? (
        <p className="ci-hbar__empty">{emptyMessage}</p>
      ) : (
        <ol className="ci-hbar__rows">
          {rows.map((row) => (
            <li key={row.key} className="ci-hbar__row" data-row={row.key}>
              <span className="ci-hbar__label" title={row.label}>
                {row.label}
              </span>
              <svg className="ci-hbar__track" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                <rect className="ci-hbar__bg" x={0} y={0} width={100} height={10} />
                {barShare(row.value, top) > 0 ? (
                  <rect className="ci-hbar__bar" data-testid="hbar-bar" x={0} y={0} width={barShare(row.value, top)} height={10} />
                ) : null}
              </svg>
              <span className="ci-hbar__value">
                <span className="ci-visually-hidden">: </span>
                {show(row)}
                {row.detail ? <span className="ci-hbar__detail"> · {row.detail}</span> : null}
              </span>
            </li>
          ))}
        </ol>
      )}
    </figure>
  )
}
